import type { FC } from "react"
import React, { useState, useRef, useEffect } from "react"
import { Box, Stack, Collapse, Divider } from "@chakra-ui/react"

import { getProposalDescription } from "proposal/helpers/description"
import MarkdownViewer from "common/components/MarkdownViewer"
import LoadMoreButton from "common/components/LoadMoreButton"

const maxHeight = 400

type Props = {
  description: string
  descriptionException?: string
  displayOriginal?: boolean
}

const ProposalDescription: FC<Props> = ({
  description,
  descriptionException,
  displayOriginal,
}) => {
  const [show, setShow] = useState(false)
  const [displayLoadMore, setDisplayLoadMore] = useState(false)
  const [contentHeight, setContentHeight] = useState(maxHeight)
  const descriptionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const checkHeight = () => {
      if (descriptionRef.current) {
        const fullHeight = descriptionRef.current.scrollHeight
        const needsCollapse = fullHeight > maxHeight
        setDisplayLoadMore(needsCollapse)
        setContentHeight(needsCollapse ? maxHeight : fullHeight)
      }
    }

    // Small delay to ensure content is rendered
    const timeoutId = setTimeout(checkHeight, 0)

    window.addEventListener("resize", checkHeight)

    return () => {
      window.removeEventListener("resize", checkHeight)
      clearTimeout(timeoutId)
    }
  }, [description, descriptionException])

  const handleToggleLoadMore = () => {
    setShow(!show)
  }

  const content =
    descriptionException && !displayOriginal
      ? getProposalDescription(descriptionException)
      : getProposalDescription(description)

  return (
    <Stack spacing={0}>
      <Box position="relative">
        <Collapse in={show} startingHeight={`${contentHeight}px`}>
          <Box
            ref={descriptionRef}
            px={{ base: 3, sm: 6 }}
            py={{ base: 3, sm: 5 }}
          >
            <MarkdownViewer content={content} />
          </Box>
        </Collapse>
        {!show && displayLoadMore && (
          <Box
            background="linear-gradient(to bottom, transparent, white)"
            bottom={0}
            height="100px"
            left={0}
            pointerEvents="none"
            position="absolute"
            right={0}
          />
        )}
      </Box>
      {displayLoadMore && (
        <Stack spacing={0}>
          <Divider />
          <Stack px={2} py={2}>
            <LoadMoreButton
              flex={1}
              label={show ? "Show less" : "Read more"}
              variant="tertiary"
              width="100%"
              onClick={handleToggleLoadMore}
            />
          </Stack>
        </Stack>
      )}
    </Stack>
  )
}

export default ProposalDescription
